/**
 * SessionList - Container component for session management
 * Follows ADR-002: Modularity - Composes focused hooks and components
 * Follows ADR-004: Separation of Concerns - Container/Presentation pattern
 */

import { useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Plus, MessageCircle, Search, RotateCw } from 'lucide-react';
import { useTranslation, type Language } from '../hooks/useTranslation';
import { useSessionList, type Session } from '../hooks/useSessionList';
import { SessionListItem } from './SessionListItem';
import { DeleteConfirmModal } from './DeleteConfirmModal';
import { getSessionTitle, getSessionDateString } from '../utils/sessionFormat';
import { log, logError } from '../utils/logger';

/**
 * Props for the SessionList component
 */
interface SessionListProps {
  /** The URL of the chat server */
  serverUrl: string;
  /** Authentication token for backend requests */
  authToken?: string;
  /** Agent ID for filtering sessions */
  agentId: string;
  /** Currently selected session ID */
  selectedSessionId?: string | null;
  /** Callback when a session is selected */
  onSessionSelect: (sessionId: string) => void;
  /** Callback when a new session should be created */
  onNewSession?: () => void;
  /** Language for the interface */
  language?: Language;
  /** Customer ID for API requests */
  customerId: string;
  /** Whether to allow deleting sessions (default: true) */
  allowDelete?: boolean;
}

/**
 * Ref interface for SessionList imperative methods
 */
export interface SessionListRef {
  refresh: () => void;
}

/**
 * SessionList component that displays and manages chat sessions
 */
export const SessionList = forwardRef<SessionListRef, SessionListProps>(
  function SessionList({
    serverUrl,
    authToken,
    agentId,
    selectedSessionId,
    onSessionSelect,
    onNewSession,
    language = 'en',
    customerId,
    allowDelete = true
  }, ref) {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [sessionToDelete, setSessionToDelete] = useState<Session | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

    const { t } = useTranslation(language);

    // Session data hook
    const {
      sessions,
      loading,
      error,
      fetchSessions,
      deleteSession
    } = useSessionList({
      serverUrl,
      authToken,
      agentId,
      customerId
    });

    // Expose refresh method
    useImperativeHandle(ref, () => ({
      refresh: () => {
        log('SessionList refresh requested');
        fetchSessions();
      }
    }), [fetchSessions]);

    /**
     * Opens the delete confirmation modal
     */
    const openDeleteModal = useCallback((session: Session) => {
      setSessionToDelete(session);
      setDeleteModalOpen(true);
    }, []);

    /**
     * Closes the delete confirmation modal
     */
    const closeDeleteModal = useCallback(() => {
      setDeleteModalOpen(false);
      setSessionToDelete(null);
      setIsDeleting(false);
    }, []);

    /**
     * Deletes a session after confirmation
     */
    const confirmDeleteSession = useCallback(async () => {
      if (!sessionToDelete) return;

      try {
        setIsDeleting(true);
        await deleteSession(sessionToDelete.id);

        if (selectedSessionId === sessionToDelete.id && onNewSession) {
          onNewSession();
        }

        closeDeleteModal();
      } catch (err) {
        logError('Error deleting session:', err);
        setIsDeleting(false);
      }
    }, [sessionToDelete, deleteSession, selectedSessionId, onNewSession, closeDeleteModal]);

    /**
     * Handles refresh button click
     */
    const handleRefresh = useCallback(async () => {
      setIsRefreshing(true);
      try {
        await fetchSessions();
      } finally {
        setIsRefreshing(false);
      }
    }, [fetchSessions]);

    // Filter sessions based on search term
    const filteredSessions = sessions.filter(session =>
      getSessionTitle(session, t).toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="chatbot-session-sidebar w-full h-full bg-card border-r border-border flex flex-col">
        {/* Header */}
        <div className="chatbot-session-sidebar-header p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="chatbot-session-sidebar-title text-lg font-semibold text-card-foreground">{t('sessions.title')}</h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                className="chatbot-session-refresh-button bg-muted hover:bg-muted text-muted-foreground p-2 rounded-lg transition-colors"
                title={t('sessions.refresh')}
                disabled={isRefreshing}
              >
                <RotateCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
              </button>
              {onNewSession && (
                <button
                  onClick={onNewSession}
                  className="chatbot-session-new-button bg-primary hover:bg-primary text-primary-foreground p-2 rounded-lg transition-all shadow-md hover:shadow-lg"
                  title={t('sessions.newChat')}
                >
                  <Plus size={20} />
                </button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="chatbot-session-search-container relative">
            <Search size={16} className="chatbot-session-search-icon absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              placeholder={t('sessions.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onTouchStart={(e) => e.stopPropagation()}
              className="chatbot-session-search-input w-full pl-10 pr-4 py-2 bg-background border border-input rounded-lg text-base text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring touch-auto"
            />
          </div>
        </div>

        {/* Sessions List */}
        <div className="chatbot-session-list flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-muted-foreground">{t('sessions.loading')}</div>
            </div>
          ) : error ? (
            <div className="chatbot-session-error p-4">
              <div className="bg-destructive text-destructive-foreground border border-destructive rounded-lg p-3">
                <p className="text-sm">{error}</p>
                <button
                  onClick={fetchSessions}
                  className="mt-2 text-sm underline hover:text-primary"
                >
                  {t('sessions.retry')}
                </button>
              </div>
            </div>
          ) : filteredSessions.length === 0 ? (
            <div className="chatbot-session-empty flex flex-col items-center justify-center h-32 text-muted-foreground">
              <MessageCircle size={32} className="mb-2 text-muted-foreground" />
              <p className="text-sm">
                {searchTerm ? t('sessions.noResults') : t('sessions.noSessions')}
              </p>
            </div>
          ) : (
            <div className="p-3 space-y-2">
              {filteredSessions.map((session) => (
                <SessionListItem
                  key={session.id}
                  id={session.id}
                  title={getSessionTitle(session, t)}
                  dateString={getSessionDateString(session, t)}
                  isSelected={selectedSessionId === session.id}
                  allowDelete={allowDelete}
                  deleteTooltip={t('sessions.delete')}
                  onSelect={onSessionSelect}
                  onDelete={() => openDeleteModal(session)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmModal
          isOpen={deleteModalOpen}
          sessionTitle={sessionToDelete ? getSessionTitle(sessionToDelete, t) : ''}
          onConfirm={confirmDeleteSession}
          onCancel={closeDeleteModal}
          language={language}
          isDeleting={isDeleting}
        />
      </div>
    );
  });

SessionList.displayName = 'SessionList';
